import { api, oneSpec, useAuthStore } from '@bemor/web-kit';
import {
    Alert,
    AlertDescription,
    AsOfBadge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    DetailRow,
    FieldGrid,
    ObjectRef,
    PageHeader,
    PublicId,
    ReasonDialog,
    StatusBadge,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import type { Approval } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import {
    approvalLabel,
    isStaffTotpReset,
    ownLocked,
    staffResetOf,
} from '@/lib/approvals';
import { useOne, useWrite } from '@/lib/queries';
import { useMe, useStaffDirectory, whoName } from '@/lib/staff';

/** The reads of `/approvals/:id` (BM-46 §2.0). */
export const approvalReads = (id: string | undefined) => ({
    approval: oneSpec(`/staff/approvals/${id ?? ''}`),
});

export function ApprovalPage() {
    const { id = '' } = useParams();
    const me = useMe();
    const directory = useStaffDirectory();
    const can = useAuthStore((s) => s.can);
    const query = useOne<Approval>(approvalReads(id).approval.path);
    const [decision, setDecision] = useState<'grant' | 'reject' | null>(null);
    const decide = useWrite(
        async (body: { decision: 'grant' | 'reject'; reason: string }) =>
            (
                await api().post<Approval>(
                    `/staff/approvals/${id}/${body.decision}`,
                    {
                        reason: body.reason || undefined,
                    },
                )
            ).data,
        ['/staff/approvals', '/staff/queues'],
    );
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Approvals', href: '/approvals' }]}
                {...detailTitle(query, query.data?.data.summary, id)}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
            />
            <DetailBody query={query} title="Approval">
                {(a) => {
                    // BM-125 decision 31: a self-approvable request (Risk's volume cap) is its requester's to decide too
                    const mine = ownLocked(a, me);
                    const may = can(a.action);
                    const pending = a.state === 'pending';
                    const reset = isStaffTotpReset(a) ? staffResetOf(a) : null;
                    const subject = reset
                        ? directory.byStaffId(reset.staffId)
                        : undefined;
                    return (
                        <div className="space-y-4">
                            {pending && mine ? (
                                <Alert data-testid="four-eyes-notice">
                                    <AlertDescription>
                                        You asked for this one: a different
                                        person with{' '}
                                        <code className="font-mono text-xs">
                                            {a.action}
                                        </code>{' '}
                                        has to decide it.
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            {pending && !mine && !may ? (
                                <Alert>
                                    <AlertDescription>
                                        Deciding needs{' '}
                                        <code className="font-mono text-xs">
                                            {a.action}
                                        </code>
                                        , which your roles do not carry.
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            <Card>
                                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                                    <CardTitle className="text-base">
                                        {a.summary}
                                        {/* BM-93: a granted "Sign in as" unlocks the link for a short window; the staff member asks again on the user's page */}
                                        {a.action ===
                                            'identity.users.impersonate' &&
                                        a.state === 'granted' ? (
                                            <span
                                                className="text-fg-2 mt-1 block text-sm font-normal"
                                                data-testid="impersonation-granted"
                                            >
                                                Granted: open the user&rsquo;s
                                                page and click Sign in as again
                                                within 15 minutes.
                                            </span>
                                        ) : null}
                                    </CardTitle>
                                    {pending && !mine && may ? (
                                        <div
                                            className="flex gap-2"
                                            data-testid="approval-actions"
                                        >
                                            <Button
                                                size="sm"
                                                onClick={() =>
                                                    setDecision('grant')
                                                }
                                            >
                                                Grant
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() =>
                                                    setDecision('reject')
                                                }
                                            >
                                                Reject
                                            </Button>
                                        </div>
                                    ) : null}
                                </CardHeader>
                                <CardContent>
                                    <FieldGrid>
                                        <DetailRow
                                            label="State"
                                            layout="stacked"
                                        >
                                            <StatusBadge
                                                status={a.state}
                                                domain="approval"
                                            />
                                            {a.reason ? (
                                                <span className="text-muted-foreground ml-2 text-xs">
                                                    {a.reason}
                                                </span>
                                            ) : null}
                                        </DetailRow>
                                        <DetailRow
                                            label="Action"
                                            layout="stacked"
                                        >
                                            {approvalLabel(a)}{' '}
                                            <span className="text-fg-2 font-mono text-xs">
                                                {a.action}
                                            </span>
                                        </DetailRow>
                                        {reset ? (
                                            <>
                                                <DetailRow
                                                    label="Staff member"
                                                    layout="stacked"
                                                >
                                                    <span data-testid="reset-subject">
                                                        {subject
                                                            ? `${subject.user.name}, ${subject.user.email}`
                                                            : reset.staffId}
                                                    </span>
                                                </DetailRow>
                                                <DetailRow
                                                    label="Their reason"
                                                    layout="stacked"
                                                >
                                                    <span data-testid="reset-reason">
                                                        {reset.reason ?? '—'}
                                                    </span>
                                                </DetailRow>
                                            </>
                                        ) : (
                                            <DetailRow
                                                label="Subject"
                                                layout="stacked"
                                            >
                                                <ObjectRef id={a.subject_id} />
                                            </DetailRow>
                                        )}
                                        <DetailRow
                                            label="Merchant"
                                            layout="stacked"
                                        >
                                            {a.merchant_id ? (
                                                <ObjectRef id={a.merchant_id} />
                                            ) : (
                                                '—'
                                            )}
                                        </DetailRow>
                                        <DetailRow
                                            label="Requested by"
                                            layout="stacked"
                                        >
                                            {whoName(
                                                a.requested_by,
                                                me,
                                                directory,
                                            )}
                                            ,{' '}
                                            <DateTime
                                                value={a.created_at}
                                                empty="—"
                                            />
                                        </DetailRow>
                                        <DetailRow
                                            label="Expires"
                                            layout="stacked"
                                        >
                                            <DateTime
                                                zone="inline"
                                                value={a.expires_at}
                                                empty="—"
                                            />
                                        </DetailRow>
                                        {a.decided_at ? (
                                            <DetailRow
                                                label="Decided"
                                                layout="stacked"
                                            >
                                                {whoName(
                                                    a.decided_by,
                                                    me,
                                                    directory,
                                                )}
                                                ,{' '}
                                                <DateTime
                                                    value={a.decided_at}
                                                    empty="—"
                                                />
                                            </DetailRow>
                                        ) : null}
                                        <DetailRow
                                            label="Payload hash"
                                            layout="stacked"
                                        >
                                            <code className="font-mono text-xs break-all">
                                                {a.payload_hash}
                                            </code>
                                        </DetailRow>
                                    </FieldGrid>
                                    {reset ? (
                                        <p
                                            className="text-fg-2 mt-4 mb-0 text-sm"
                                            data-testid="reset-effect"
                                        >
                                            On grant, Identity clears this
                                            person's authenticator and recovery
                                            codes and ends every session they
                                            have; their next sign-in sets up a
                                            new authenticator.
                                        </p>
                                    ) : null}
                                    <p className="text-muted-foreground mt-4 mb-1 text-xs">
                                        Exactly what the owning service will
                                        execute
                                    </p>
                                    <pre
                                        className="bg-muted overflow-x-auto rounded p-2 text-xs"
                                        data-testid="approval-payload"
                                    >
                                        {JSON.stringify(a.payload, null, 2)}
                                    </pre>
                                </CardContent>
                            </Card>
                            {decision ? (
                                <ReasonDialog
                                    open
                                    onOpenChange={(o) =>
                                        o ? null : setDecision(null)
                                    }
                                    title={
                                        decision === 'grant'
                                            ? 'Grant this request'
                                            : 'Reject this request'
                                    }
                                    description={
                                        decision === 'grant'
                                            ? 'The owning service executes the payload above, and only that payload.'
                                            : 'The requester is told why; the action is not executed.'
                                    }
                                    confirmLabel={
                                        decision === 'grant'
                                            ? 'Grant'
                                            : 'Reject'
                                    }
                                    destructive={decision === 'reject'}
                                    required={decision === 'reject'}
                                    busy={decide.isPending}
                                    error={decide.error}
                                    onConfirm={(reason) =>
                                        decide.mutate(
                                            { decision, reason },
                                            {
                                                onSuccess: () =>
                                                    setDecision(null),
                                            },
                                        )
                                    }
                                />
                            ) : null}
                        </div>
                    );
                }}
            </DetailBody>
        </div>
    );
}
